import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { FloatingParticles } from '../../components/animations/FloatingParticles';
import { Mail, Lock, Eye, EyeOff, AlertCircle, CheckCircle } from 'lucide-react';
import apiClient from '../../utils/apiClient';

const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
});

type LoginFormData = z.infer<typeof loginSchema>;

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [interviewLink, setInterviewLink] = useState<string | null>(null);
  const [showInterviewSuccess, setShowInterviewSuccess] = useState(false);
  const [isExistingInterview, setIsExistingInterview] = useState(false);
  const [startingInterview, setStartingInterview] = useState(false);

  const from = location.state?.from?.pathname || '/dashboard';
  const jobId = location.state?.jobId;

  // Function to start interview session after application
  const handleStartInterview = async () => {
    if (!jobId) return;

    setStartingInterview(true);
    try {
      const response = await apiClient.post('/interviews/', { jobId: jobId });

      if (response.data.success) {
        setInterviewLink(response.data.data.interviewUrl);
        setIsExistingInterview(response.data.data.isExisting || false);
        // Navigate to interview immediately
        navigate(response.data.data.interviewUrl);
      }
    } catch (error) {
      setError('Failed to start interview. Please try again.');
    } finally {
      setStartingInterview(false);
    }
  };

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      setError(null);
      await login(data.email, data.password);

      // Simply navigate to the intended destination after login
      // Let the destination page handle application logic
      if (jobId) {
        navigate(`/careers/${jobId}`, {
          state: { checkStatusOnly: true },
          replace: true
        });
      } else {
        navigate(from, { replace: true });
      }
    } catch (err: any) {
      setError(err.message || 'Invalid email or password');
    }
  };

  return (
    <div className="min-h-screen bg-black relative overflow-hidden">
      <FloatingParticles />
      
      {/* Background Effects */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-950 via-gray-900 to-black pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-tr from-amber-500/5 via-transparent to-yellow-500/5 pointer-events-none" />
      
      <div className="relative z-10 flex items-center justify-center min-h-screen px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 50, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6 }}
          className="max-w-md w-full"
        >
          <div className="bg-white/5 backdrop-blur-2xl border border-white/10 rounded-2xl sm:rounded-3xl p-8 sm:p-10 shadow-2xl hover:shadow-amber-500/10 hover:bg-white/[0.07] hover:border-white/15 transition-all duration-300">
            {/* Header */}
            <div className="text-center mb-8">
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
                className="w-20 h-20 bg-gradient-to-br from-gray-900/90 to-black/95 backdrop-blur-2xl rounded-full flex items-center justify-center mx-auto mb-6 shadow-2xl shadow-black/80 ring-1 ring-white/10"
              >
                <img src="/assets/images/branding/Website-Icon.png" alt="MTAI Logo" className="w-10 h-10" />
              </motion.div>
              
              <motion.h1 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-3xl sm:text-4xl font-bold text-gradient-primary mb-3 leading-tight"
              >
                Welcome Back!
              </motion.h1>
              
              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-gray-300 text-lg"
              >
                Sign in to access your AI-powered recruitment journey
              </motion.p>
            </div>

            {/* Error Alert */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -20, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -20, scale: 0.9 }}
                className="mb-6 p-4 bg-gradient-to-br from-red-500/20 to-red-600/30 backdrop-blur-2xl border border-red-500/30 rounded-xl flex items-center gap-3"
              >
                <div className="w-10 h-10 bg-red-500/30 backdrop-blur-xl rounded-full flex items-center justify-center border border-red-400/30">
                  <AlertCircle className="w-5 h-5 text-red-300" />
                </div>
                <div className="flex-1">
                  <p className="text-red-100 font-medium text-sm">{error}</p>
                </div>
              </motion.div>
            )}

            <motion.form 
              onSubmit={handleSubmit(onSubmit)} 
              className="space-y-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
            >
              {/* Email Field */}
              <div>
                <label className="block text-sm font-semibold text-gray-200 mb-3">
                  Email Address
                </label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Mail className="w-5 h-5 text-gray-400 group-focus-within:text-amber-400 transition-colors duration-200" />
                  </div>
                  <input
                    type="email"
                    {...register('email')}
                    className="w-full pl-12 pr-4 py-4 bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-400/50 focus:border-amber-400/50 focus:bg-white/10 transition-all duration-300 hover:bg-white/[0.08] hover:border-white/20 autofill:bg-white/5 autofill:text-white autofill:shadow-[inset_0_0_0px_1000px_rgb(255_255_255_/_0.05)]"
                    placeholder="you@example.com"
                  />
                </div>
                {errors.email && (
                  <motion.p 
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-2 text-sm text-red-400 flex items-center gap-2"
                  >
                    <AlertCircle className="w-4 h-4" />
                    {errors.email.message}
                  </motion.p>
                )}
              </div>

              {/* Password Field */}
              <div>
                <label className="block text-sm font-semibold text-gray-200 mb-3">
                  Password
                </label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Lock className="w-5 h-5 text-gray-400 group-focus-within:text-amber-400 transition-colors duration-200" />
                  </div>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    {...register('password')}
                    className="w-full pl-12 pr-12 py-4 bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-400/50 focus:border-amber-400/50 focus:bg-white/10 transition-all duration-300 hover:bg-white/[0.08] hover:border-white/20 autofill:bg-white/5 autofill:text-white autofill:shadow-[inset_0_0_0px_1000px_rgb(255_255_255_/_0.05)]"
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-amber-400 transition-colors duration-200"
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <motion.p 
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-2 text-sm text-red-400 flex items-center gap-2"
                  >
                    <AlertCircle className="w-4 h-4" />
                    {errors.password.message}
                  </motion.p>
                )}
              </div>

              {/* Remember Me & Forgot Password */}
              <div className="flex items-center justify-between">
                <label className="flex items-center group cursor-pointer">
                  <input
                    type="checkbox"
                    className="w-4 h-4 bg-white/10 border border-white/20 rounded text-amber-500 focus:ring-amber-400/50 focus:ring-2"
                  />
                  <span className="ml-3 text-sm text-gray-300 group-hover:text-white transition-colors duration-200">
                    Remember me
                  </span>
                </label>
                <Link
                  to="/forgot-password"
                  className="text-sm font-medium text-amber-400 hover:text-amber-300 transition-colors duration-200 hover:underline"
                >
                  Forgot password?
                </Link>
              </div>

              {/* Submit Button */}
              <motion.button
                type="submit"
                disabled={isSubmitting}
                whileHover={!isSubmitting ? { scale: 1.02, y: -2 } : {}}
                whileTap={!isSubmitting ? { scale: 0.98 } : {}}
                className="w-full py-4 px-6 bg-gradient-to-r from-amber-500 to-yellow-500 text-black font-bold text-lg rounded-xl hover:from-amber-400 hover:to-yellow-400 focus:outline-none focus:ring-2 focus:ring-amber-400/50 focus:ring-offset-2 focus:ring-offset-black transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 shadow-xl hover:shadow-amber-500/25"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-3">
                    <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                    Signing you in...
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    Sign In
                  </span>
                )}
              </motion.button>
            </motion.form>

            {/* Sign Up Link */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="mt-8 text-center"
            >
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/10"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-gradient-to-b from-transparent from-10% via-gray-900 to-transparent to-90% text-gray-400">
                    New to MTAI?
                  </span>
                </div>
              </div>
              
              <Link
                to="/register"
                className="mt-4 inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 font-semibold transition-colors duration-200 underline hover:underline"
              >
                Create your account
              </Link>
            </motion.div>
          </div>
        </motion.div>
      </div>

      {/* Application Success Modal */}
      {showInterviewSuccess && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-xl flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 50 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 50 }}
            className="relative bg-white/10 backdrop-blur-2xl border border-white/20 rounded-2xl sm:rounded-3xl p-8 sm:p-10 max-w-md w-full shadow-2xl overflow-hidden"
          >
            {/* Background gradient */}
            <div className="absolute inset-0 bg-gradient-to-br from-amber-500/10 via-yellow-500/5 to-amber-600/10 pointer-events-none"></div>
            
            <div className="relative z-10 text-center">
              <div className="w-20 h-20 bg-gradient-to-br from-green-500/30 to-emerald-500/30 backdrop-blur-xl rounded-full flex items-center justify-center mx-auto mb-6 border border-green-400/30 shadow-xl">
                <CheckCircle className="w-10 h-10 text-green-300" />
              </div>
              
              <h3 className="text-2xl font-bold bg-gradient-to-r from-white via-green-100 to-green-200 bg-clip-text text-transparent mb-3">
                Application Successful!
              </h3>
              
              <p className="text-gray-300 text-lg mb-8 leading-relaxed">
                You have successfully applied for this position. You can now start your AI interview to complete your application.
              </p>

              <div className="space-y-4">
                <motion.button
                  onClick={handleStartInterview}
                  disabled={startingInterview}
                  whileHover={!startingInterview ? { scale: 1.02, y: -2 } : {}}
                  whileTap={!startingInterview ? { scale: 0.98 } : {}}
                  className="w-full py-4 px-6 bg-gradient-to-r from-amber-500 to-yellow-500 text-black font-bold text-lg rounded-xl hover:from-amber-400 hover:to-yellow-400 transition-all duration-300 disabled:opacity-50 shadow-xl hover:shadow-amber-500/25"
                >
                  {startingInterview ? (
                    <span className="flex items-center justify-center gap-3">
                      <div className="w-5 h-5 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                      Starting Interview...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-3">
                      🚀 Start AI Interview Now
                    </span>
                  )}
                </motion.button>
                
                <motion.button
                  onClick={() => navigate('/dashboard')}
                  whileHover={{ scale: 1.02, y: -1 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full py-4 px-6 bg-white/10 backdrop-blur-xl border border-white/20 text-white font-semibold rounded-xl hover:bg-white/20 transition-all duration-300"
                >
                  Save for Later - Go to Dashboard
                </motion.button>
              </div>
              
              <p className="text-gray-400 text-sm mt-6 bg-white/5 backdrop-blur-sm rounded-xl p-3 border border-white/10">
                You can also start the interview from your dashboard anytime
              </p>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;